/**
 * Per-brand colour themes for the authenticated portals (customer portal and
 * admin workspace).
 *
 * When the selected account resolves to a property whose directory `brand`
 * has an entry here, AppLayout sets `data-brand="<key>"` on <html> and the
 * matching block in packages/ui/src/themes/brands.css re-points the semantic tokens. The sidebar
 * background is passed to AppShell directly (it is a prop, not a token).
 *
 * Light-mode palettes only, per the brand style sheets; dark mode keeps the
 * PazNetworks palette so contrast stays under our control.
 */
export interface BrandTheme {
  /** Links and buttons → --primary */
  primary: string;
  /** Navigation highlight → --nav-highlight (active sidebar row) */
  navHighlight: string;
  /** Titles and icons → --heading */
  heading: string;
  /** Sidebar text → --sidebar-text */
  sidebarText: string;
  /** Text on the active navigation row → --nav-highlight-text (defaults to sidebar text). */
  navHighlightText?: string;
  /** Sidebar background → AppShell portalColor */
  sidebarBackground: string;
  /** Map background → --map-bg (property globe) */
  mapBackground: string;
  /** Signature accent → --brand-accent (active-row edge, brand header rule). Non-text use only. */
  accent?: string;
  /** Card / row border → --border and --divider. */
  border?: string;
  /** Wordmark for the dark sidebar header (white on brand background). */
  sidebarLogo?: string;
  /** Brand word(s) stripped from the start of the property name in the sidebar header. */
  namePrefix?: RegExp;
}

export const BRAND_THEMES: Record<string, BrandTheme> = {
  hilton: {
    primary: '#002f61',
    navHighlight: '#1369c2',
    heading: '#37193c',
    sidebarText: '#f1f1f1',
    sidebarBackground: '#002f61',
    mapBackground: '#f3f5f9',
    sidebarLogo: '/brands/hilton-white.png', // licensed white wordmark (owner-supplied 2026-09-20)
    namePrefix: /^hilton\s+/i,
  },
  marriott: {
    primary: '#98133a',
    navHighlight: '#98133a',
    navHighlightText: '#ffffff',
    heading: '#37193c',
    sidebarText: '#000000',
    sidebarBackground: '#ffffff',
    mapBackground: '#f3f5f9',
    sidebarLogo: '/brands/marriott-mark.png', // owner-supplied mark + wordmark (2026-09-21)
    // "Sacramento Marriott Rancho Cordova" keeps its full name — the brand word is mid-name.
  },
  luma: {
    primary: '#00aeef',
    navHighlight: '#00aeef',
    // No navHighlightText: charcoal sidebar text stays on the bright-blue row (white would fail contrast).
    heading: '#232323',
    sidebarText: '#232323',
    sidebarBackground: '#ffffff',
    mapBackground: '#f5f5f5',
    sidebarLogo: '/brands/luma-mark.svg', // owner-supplied footer wordmark, recoloured #fff → #232323 for the white sidebar
    namePrefix: /^luma\s+(hotel\s+)?/i,
  },
  sheraton: {
    primary: '#1c1c1c',
    navHighlight: '#666666',
    navHighlightText: '#ffffff', // #1c1c1c on #666666 is ~2.6:1; white is ~5.7:1
    heading: '#1c1c1c',
    sidebarText: '#1c1c1c',
    sidebarBackground: '#ffffff',
    mapBackground: '#f4f4f4',
    sidebarLogo: '/brands/sheraton-mark.png', // owner-supplied grey (#666) mark + wordmark on transparent (2026-09-21)
    namePrefix: /^sheraton\s+/i,
  },
  doubletree: {
    // Values read from Hilton's own DoubleTree pages (computed styles), owner-supplied 2026-09-21.
    primary: '#09194e',          // Hilton Navy — primary CTA
    navHighlight: '#09194e',
    navHighlightText: '#ffffff',
    heading: '#0a0a0a',
    sidebarText: '#121212',
    sidebarBackground: '#ffffff',
    mapBackground: '#f5f5f5',
    accent: '#a3cd39',           // DoubleTree lime — identity accent (fails text contrast on white; edges/rules only)
    border: '#e5e7eb',
    sidebarLogo: '/brands/doubletree-mark.png', // supplied mark, property line cropped off so it serves PHX and HWD
    namePrefix: /^double\s?tree\s+(suites\s+)?(by\s+hilton\s+)?(hotel\s+)?/i,
  },
};

/** Relative luminance of a #rrggbb colour (0 = black, 1 = white). */
export function hexLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** True when the brand sidebar is light, so marks meant for dark surfaces must swap. */
export function sidebarIsLight(theme: BrandTheme): boolean {
  return hexLuminance(theme.sidebarBackground) > 0.6;
}

/** "Hilton San Francisco Airport Bayfront" → "San Francisco Airport Bayfront" under the Hilton wordmark. */
export function sidebarPropertyName(name: string, theme: BrandTheme): string {
  const stripped = theme.namePrefix ? name.replace(theme.namePrefix, '') : name;
  return stripped.trim() || name;
}

export function resolveBrandTheme(brand: string | null | undefined): { key: string; theme: BrandTheme } | null {
  const key = brand?.trim().toLowerCase();
  if (!key) return null;
  const theme = BRAND_THEMES[key];
  return theme ? { key, theme } : null;
}

/** `/locations/<id>` and `/locations/<id>/…` → `<id>`; anything else → null. */
export function locationIdFromPath(pathname: string): string | null {
  const m = /^\/locations\/([^/?#]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Which brand the current view should wear.
 *  1. A property page (`/locations/:id`) always wears that property's brand —
 *     also in the "All accounts" view, which is the default landing state.
 *  2. Otherwise a single selected account wears its property's brand.
 *  3. Portfolio-wide views (dashboard, lists) have no single brand → null.
 */
export function pickViewBrand(input: {
  pathname: string;
  isPortfolio: boolean;
  locations: ReadonlyArray<{ id: string; name: string; directory: { brand: string | null } | null }>;
  activeAccountName: string | null | undefined;
}): { key: string; theme: BrandTheme; title: string } | null {
  const routeId = locationIdFromPath(input.pathname);
  if (routeId) {
    const loc = input.locations.find((l) => l.id === routeId);
    const resolved = resolveBrandTheme(loc?.directory?.brand);
    if (loc && resolved) return { ...resolved, title: sidebarPropertyName(loc.name, resolved.theme) };
  }
  if (!input.isPortfolio) {
    const brand = input.locations.map((l) => l.directory?.brand).find((b): b is string => !!b);
    const resolved = resolveBrandTheme(brand);
    if (resolved) {
      const name = input.activeAccountName ?? input.locations[0]?.name ?? '';
      return { ...resolved, title: sidebarPropertyName(name, resolved.theme) };
    }
  }
  return null;
}
